package com.example.positionahead.ui.components

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.unit.dp
import com.example.positionahead.model.PricePoint
import com.example.positionahead.ui.theme.gainColor
import com.example.positionahead.ui.theme.lossColor
import kotlin.math.max
import kotlin.math.min

@Composable
fun StockLineChart(
    points: List<PricePoint>,
    modifier: Modifier = Modifier,
    heightDp: Int = 160,
) {
    if (points.size < 2) return

    val closes = points.map { it.close }
    val minY = closes.min()
    val maxY = closes.max()
    val range = max(maxY - minY, maxY * 0.02)
    val paddedMin = minY - range * 0.05
    val paddedMax = maxY + range * 0.05
    val isUp = closes.last() >= closes.first()
    val lineColor = if (isUp) gainColor else lossColor
    val gridColor = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.08f)

    Canvas(
        modifier = modifier
            .fillMaxWidth()
            .height(heightDp.dp),
    ) {
        val chartLeft = 8f
        val chartRight = size.width - 8f
        val chartTop = 8f
        val chartBottom = size.height - 8f
        val chartWidth = chartRight - chartLeft
        val chartHeight = chartBottom - chartTop

        repeat(4) { index ->
            val y = chartTop + chartHeight * index / 3f
            drawLine(
                color = gridColor,
                start = Offset(chartLeft, y),
                end = Offset(chartRight, y),
                strokeWidth = 1f,
                pathEffect = PathEffect.dashPathEffect(floatArrayOf(8f, 8f)),
            )
        }

        fun xAt(index: Int): Float {
            if (points.size == 1) return chartLeft
            return chartLeft + chartWidth * index / (points.size - 1)
        }

        fun yAt(value: Double): Float {
            val fraction = ((value - paddedMin) / (paddedMax - paddedMin)).toFloat().coerceIn(0f, 1f)
            return chartBottom - fraction * chartHeight
        }

        val fillPath = Path().apply {
            moveTo(xAt(0), yAt(closes.first()))
            closes.forEachIndexed { index, close ->
                if (index > 0) lineTo(xAt(index), yAt(close))
            }
            lineTo(xAt(closes.lastIndex), chartBottom)
            lineTo(xAt(0), chartBottom)
            close()
        }
        drawPath(
            path = fillPath,
            color = lineColor.copy(alpha = 0.12f),
        )

        val linePath = Path().apply {
            moveTo(xAt(0), yAt(closes.first()))
            closes.forEachIndexed { index, close ->
                if (index > 0) lineTo(xAt(index), yAt(close))
            }
        }
        drawPath(
            path = linePath,
            color = lineColor,
            style = Stroke(width = 2.5f),
        )
    }
}
